import React, { useEffect, useState, useRef, useCallback } from "react"
import { Form, Spinner } from "reactstrap"
import moment from "moment"
import helper from "helpers/fn_helper"
import { UploadFileToS3, DeleteFileFromS3 } from "services/api/module/UploadS3"

const ListUploadFileApi = ({
  onChange = () => console.log("onChange!"),
  value = [],
  maxFiles = 5,
  maxSize = 10,
  widthSize = 0,
  heightSize = 0,
  error = null,
  touched = false,
}) => {
  const [urls, setUrls] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (value?.length > 0) {
      if (typeof value[0] === "object" && value[0].hasOwnProperty("image")) {
        setUrls(value.map(item => item.image))
      } else {
        setUrls(value)
      }
    }
  }, [value])

  const formatUrlsForOnChange = urlArray => {
    return urlArray.map((url, index) => ({
      image: url,
      text: (index + 1).toString(),
    }))
  }

  const removeFile = useCallback(
    async index => {
      try {
        setIsDeleting(true)
        const fileUrl = urls[index]

        const deleteResult = await DeleteFileFromS3({
          folder: "questionnaireimage",
          fileUrl: fileUrl,
        })

        if (deleteResult.success) {
          console.log(`File deleted successfully from S3: ${fileUrl}`)
        } else {
          console.error(
            `Failed to delete file from S3: ${fileUrl}`,
            deleteResult.error
          )
        }

        setUrls(prevUrls => {
          const newUrls = [...prevUrls]
          newUrls.splice(index, 1)

          const formattedData = formatUrlsForOnChange(newUrls)
          console.log(formattedData)
          onChange(formattedData)
          return newUrls
        })
      } catch (error) {
        console.error(`Error removing file at index ${index}:`, error)
      } finally {
        setIsDeleting(false)
      }
    },
    [urls, onChange]
  )

  const handleFileChange = async event => {
    if (isUploading) return

    const file = event.target.files[0]
    if (!file) return

    if (urls.length + 1 > maxFiles) {
      alert(`Max ${maxFiles} images`)
      event.target.value = ""
      return
    }

    const typeFile = file.name.split(".").pop().toLowerCase()
    const fileSize = file.size / 1024 / 1024
    const { width, height } = await helper.FNFILE.getImageDimensions(file)

    console.log(width, height)

    if (!["png", "jpg", "jpeg"].includes(typeFile)) {
      alert(
        `File "${file.name}" has invalid type. Only png, jpg, jpeg are allowed.`
      )
      event.target.value = ""
      return
    }

    if (fileSize > maxSize) {
      alert(`File "${file.name}" exceeds size limit of ${maxSize} MB`)
      event.target.value = ""
      return
    }

    // เช็คขนาดรูปภาพ - อนุญาตให้รูปที่มีขนาดเท่ากับหรือเล็กกว่าที่กำหนด
    if (
      widthSize !== 0 &&
      heightSize !== 0 &&
      (width > widthSize || height > heightSize)
    ) {
      alert(
        `File "${file.name}" has resolution too large. Maximum allowed: ${widthSize} x ${heightSize}, but got: ${width} x ${height}`
      )
      event.target.value = ""
      return
    }

    setIsUploading(true)
    await uploadFile(file)
    event.target.value = ""
  }

  const uploadFile = async file => {
    try {
      const typeFile = file.name.split(".").pop().toLowerCase()

      const originalNameWithSpaces = file.name.split(".")[0]
      const originalName = originalNameWithSpaces.replace(/\s+/g, "_")

      const timestamp = moment()
      const formattedDate = timestamp.format("DDMMYYYY")
      const formattedTime = timestamp.format("HHmmss")
      const fileName = `${originalName}_${formattedDate}_${formattedTime}.${typeFile}`

      console.log(`Original filename: ${file.name} -> Sanitized: ${fileName}`)

      const response = await UploadFileToS3({
        file: file,
        folder: "questionnaireimage",
        fileName: fileName,
        contentType: `image/${typeFile}`,
      })

      if (response.success) {
        const updatedUrls = [...urls, response.url]
        setUrls(updatedUrls)

        const formattedData = formatUrlsForOnChange(updatedUrls)
        onChange(formattedData)
      }
    } catch (error) {
      console.error(`Error uploading file:`, error)
    } finally {
      setIsUploading(false)
    }
  }

  // สร้าง text สำหรับแสดงข้อมูล resolution
  const getResolutionText = () => {
    if (widthSize !== 0 && heightSize !== 0) {
      return `Maximum resolution ${widthSize} x ${heightSize}`
    }
    return ""
  }

  return (
    <div className="mb-3">
      {urls.length < maxFiles && (
        <div
          className={`${urls.length > 0 ? "mb-3" : ""} ${
            error && touched ? "is-invalid" : ""
          }`}
        >
          <input
            type="file"
            ref={fileInputRef}
            accept=".png,.jpg,.jpeg"
            onChange={handleFileChange}
            style={{ display: "none" }}
            disabled={isUploading || isDeleting}
          />
          <button
            type="button"
            className={`btn btn-primary ${
              error && touched ? "border-danger" : ""
            }`}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isDeleting}
          >
            {isUploading ? (
              <div className="d-flex align-items-center">
                <Spinner size="sm" className="me-2" />
                Uploading
              </div>
            ) : (
              "Upload Image"
            )}
          </button>
          <span className="ms-2">
            Max {maxFiles} images, {maxSize} MB per image, .jpg/.png only
            {getResolutionText() && `, ${getResolutionText()}`}.
          </span>
          {error && touched && (
            <div className="invalid-feedback d-block">{error}</div>
          )}
        </div>
      )}

      <div className="d-flex flex-column gap-3">
        {urls.map((url, index) => (
          <div
            key={index}
            className="position-relative "
            style={{
              width: 200,
              height: 200,
            }}
          >
            <div className="">
              <img
                src={url}
                alt={`preview ${index + 1}`}
                style={{
                  width: 200,
                  height: 200,
                }}
              />
              <div className="position-absolute bottom-0 left-0 bg-primary text-white px-2 py-1">
                {index + 1}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
              onClick={() => removeFile(index)}
              disabled={isUploading || isDeleting}
            >
              {isDeleting ? (
                <Spinner size="sm" />
              ) : (
                <i className="bx bx-trash"></i>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ListUploadFileApi
